import React, { useState, useContext, useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  TablePagination,
  Button,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  TableSortLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Chip,
} from '@mui/material';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import { styled } from '@mui/material/styles';
import SettingsIcon from '@mui/icons-material/Settings';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ConfirmationDialog from '../components/ConfirmationDialog';
import InfoModalV2 from '../components/InfoModalV2';
import { createSeedSchema } from '../modals/SeedSchema';
import useIsMobile from '../utils/useIsMobile';
import AddSeed from './AddSeed';
import { doc, deleteDoc, addDoc, collection } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { AppContext } from '../AppContext';

function Seeds({ setError, setSuccess }) {
  const { user, seeds: allSeeds = [], userData } = useContext(AppContext);
  const activeFarmId = userData?.activeFarmId;
  const isMobile = useIsMobile();

  // Local states for dialog, menu, search & pagination
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [seedToEdit, setSeedToEdit] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedSeed, setSelectedSeed] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmSeed, setConfirmSeed] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Sorting state
  const [seedsSortColumn, setSeedsSortColumn] = useState('name');
  const [seedsSortDirection, setSeedsSortDirection] = useState('asc');

  // State for the InfoModal (displaying full details)
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [selectedSeedData, setSelectedSeedData] = useState(null);

  // Filter seeds by search term (by name)
  const filteredSeeds = useMemo(() => {
    return allSeeds.filter(seed =>
      seed.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [allSeeds, searchTerm]);

  // Sorting helper: sort the filtered seeds array.
  const sortedSeeds = useMemo(() => {
    const sorted = [...filteredSeeds].sort((a, b) => {
      let aValue, bValue;
      switch (seedsSortColumn) {
        case 'name':
          aValue = a.name ? a.name.toLowerCase() : '';
          bValue = b.name ? b.name.toLowerCase() : '';
          break;
        case 'soakHours':
          aValue = parseFloat(a.soakHours) || 0;
          bValue = parseFloat(b.soakHours) || 0;
          break;
        case 'seedPrice':
          aValue = parseFloat(a.seedPrice) || 0;
          bValue = parseFloat(b.seedPrice) || 0;
          break;
        case 'germination':
          aValue = parseFloat(a.blackout) || 0;
          bValue = parseFloat(b.blackout) || 0;
          break;
        case 'blackout':
          aValue = parseFloat(a.blackout) || 0;
          bValue = parseFloat(b.blackout) || 0;
          break;
        case 'packetWeight':
          aValue = parseFloat(a.packetWeight) || 0;
          bValue = parseFloat(b.packetWeight) || 0;
          break;
        default:
          aValue = '';
          bValue = '';
      }
      if (aValue < bValue) return seedsSortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return seedsSortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredSeeds, seedsSortColumn, seedsSortDirection]);

  // Paginate the sorted results.
  const paginatedSeeds = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return sortedSeeds.slice(startIndex, startIndex + rowsPerPage);
  }, [sortedSeeds, page, rowsPerPage]);

  // Handler to change sorting.
  const handleSeedsSort = (column) => {
    if (seedsSortColumn === column) {
      setSeedsSortDirection(seedsSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSeedsSortColumn(column);
      setSeedsSortDirection('asc');
    }
  };

  // Dialog handlers
  const handleOpen = () => setOpen(true);
  const handleCloseDialog = () => {
    setOpen(false);
    setEditMode(false);
    setSeedToEdit(null);
  };
  const handleAddSeed = () => {
    handleCloseDialog();
  };

  const handleClone = async () => {
    if (!selectedSeed || !user) return;
    if (!activeFarmId) {
      setError?.('No active farm selected.');
      return;
    }
    try {
      const { id, ...dataToClone } = selectedSeed;
      await addDoc(collection(db, 'farms', activeFarmId, 'seeds'), dataToClone);
      setSuccess?.('Seed cloned successfully');
    } catch (err) {
      console.error('Error cloning seed:', err);
      setError?.('Failed to clone seed');
    }
    handleMenuClose();
  };

  const handleDelete = async (id) => {
    if (!user) return;
    if (!activeFarmId) {
      setError?.('No active farm selected.');
      return;
    }
    try {
      await deleteDoc(doc(db, 'farms', activeFarmId, 'seeds', id));
      setSuccess?.('Seed deleted successfully');
    } catch (error) {
      console.error('Error deleting seed:', error);
      setError?.('Failed to delete seed');
    }
    handleMenuClose();
  };

  // Confirmation dialog for deletion
  const openConfirmDialog = (seed) => {
    setConfirmSeed(seed);
    setConfirmOpen(true);
  };
  const handleConfirmDelete = async () => {
    if (confirmSeed) {
      await handleDelete(confirmSeed.id);
    }
    setConfirmOpen(false);
    setConfirmSeed(null);
  };
  const handleConfirmCancel = () => {
    setConfirmOpen(false);
    setConfirmSeed(null);
  };

  // Menu handlers for seed actions
  const handleMenuOpen = (event, seed) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedSeed(seed);
  };
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedSeed(null);
  };

  // Edit action
  const handleEdit = () => {
    setEditMode(true);
    setSeedToEdit(selectedSeed);
    handleOpen();
    handleMenuClose();
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // New: Row click handler to open the info modal
  const handleRowClick = (seed) => {
    setSelectedSeedData(seed);
    setInfoModalOpen(true);
  };

  const seedSchema = useMemo(() => createSeedSchema(), []);

  const subscriptionActive = isSubscriptionActive(userData);

  const getPacketCostString = (seed) => {
    if (!seed?.seedPrice || Number.isNaN(parseFloat(seed.seedPrice))) return 'N/A';
    return `${seed.priceCurrency || ''}${seed.seedPrice}`.trim() || 'N/A';
  };

  const getPacketWeightString = (seed) => {
    if (seed?.packetWeight == null || seed.packetWeight === '') return 'N/A';

    if (seed.weightUnit === 'g') {
      return seed.packetWeight >= 1000
        ? `${(seed.packetWeight / 1000).toFixed(2)}kg`
        : `${seed.packetWeight}g`;
    }

    if (seed.weightUnit === 'oz') {
      return seed.packetWeight >= 16
        ? `${(seed.packetWeight / 16).toFixed(2)}lb`
        : `${seed.packetWeight}oz`;
    }

    return `${seed.packetWeight}${seed.weightUnit || ''}`.trim() || 'N/A';
  };

  const withDays = (value) => (value ? `${value} days` : 'N/A');
  const withHours = (value) => (value ? `${value} hours` : 'N/A');

  // Styled table cell for header styling
  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
    },
    '& .MuiTableSortLabel-root': {
      color: `${theme.palette.common.white} !important`,
      '&.Mui-active': {
        color: `${theme.palette.common.white} !important`,
      },
      '& .MuiTableSortLabel-icon': {
        color: `${theme.palette.common.white} !important`,
      },
      '&:hover': {
        color: 'rgba(255, 255, 255, 0.75) !important',
      },
    },
  }));

  return (
    <Box
      sx={{
        backgroundColor: (theme) => theme.palette.background.default,
        color: (theme) => theme.palette.text.primary,
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: (theme) => theme.palette.background.default,
          zIndex: -1,
        },
      }}
    >
      <Typography variant="h5" sx={{ mb: 2 }}>Seeds</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ 
          maxWidth: { xs: 175, sm: 175, md: 300 }
          }}>
          <TextField
            label="Search by Seed Name"
            variant="outlined"
            size="small"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Box>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleOpen}
          disabled={!subscriptionActive}
        >
          Add Seed
        </Button>
      </Box>
      {isMobile && (
        <Paper sx={{ mb: 2 }}>
          <List disablePadding>
            {paginatedSeeds.map((seed) => (
              <React.Fragment key={seed.id}>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => handleRowClick(seed)} sx={{ alignItems: 'flex-start' }}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle2">{seed.name}</Typography>
                          <Chip size="small" color="info" label={getPacketCostString(seed)} />
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="caption" sx={{ display: 'block' }}>
                            Soak {seed.soakHours ? `${seed.soakHours}h` : '-'} · Germ {seed.germination ? `${seed.germination}d` : '-'} · Blackout {seed.blackout ? `${seed.blackout}d` : '-'}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block' }}>
                            Water {seed.wateringFrequency ? `${seed.wateringFrequency} ${seed.wateringIntervalUnit === 'weeks' ? 'wk' : 'd'}` : '-'}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
            {paginatedSeeds.length === 0 && (
              <ListItem>
                <ListItemText primary="No seeds found." />
              </ListItem>
            )}
          </List>
        </Paper>
      )}
      <TableContainer component={Paper} sx={{ display: { xs: 'none', sm: 'block' } }}>
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell>
                <TableSortLabel
                  active={seedsSortColumn === 'name'}
                  direction={seedsSortColumn === 'name' ? seedsSortDirection : 'asc'}
                  onClick={() => handleSeedsSort('name')}
                >
                  Name
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell>
                <TableSortLabel
                  active={seedsSortColumn === 'seedPrice'}
                  direction={seedsSortColumn === 'seedPrice' ? seedsSortDirection : 'asc'}
                  onClick={() => handleSeedsSort('seedPrice')}
                >
                  Packet Cost
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell>
                <TableSortLabel
                  active={seedsSortColumn === 'packetWeight'}
                  direction={seedsSortColumn === 'packetWeight' ? seedsSortDirection : 'asc'}
                  onClick={() => handleSeedsSort('packetWeight')}
                >
                  Packet Weight
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell>
                <TableSortLabel
                  active={seedsSortColumn === 'soakHours'}
                  direction={seedsSortColumn === 'soakHours' ? seedsSortDirection : 'asc'}
                  onClick={() => handleSeedsSort('soakHours')}
                >
                  Soak
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell>
                <TableSortLabel
                  active={seedsSortColumn === 'germination'}
                  direction={seedsSortColumn === 'germination' ? seedsSortDirection : 'asc'}
                  onClick={() => handleSeedsSort('germination')}
                >
                  Germination
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell>
                <TableSortLabel
                  active={seedsSortColumn === 'blackout'}
                  direction={seedsSortColumn === 'blackout' ? seedsSortDirection : 'asc'}
                  onClick={() => handleSeedsSort('blackout')}
                >
                  Blackout
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>Notes</span>
                  <Tooltip title={<Typography sx={{ fontSize: '0.8rem' }}>Mouse over note to read entire entry.</Typography>}>
                    <HelpOutlineIcon fontSize="small" sx={{ cursor: 'pointer' }} />
                  </Tooltip>
                </Box>
              </StyledTableCell>
              <StyledTableCell>Actions</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedSeeds.map((seed) => (
              // Make the row clickable to open the details modal.
              <TableRow
                key={seed.id}
                onClick={() => handleRowClick(seed)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>{seed.name || 'N/A'}</TableCell>
                <TableCell>{getPacketCostString(seed)}</TableCell>
                <TableCell>{getPacketWeightString(seed)}</TableCell>
                <TableCell>{withHours(seed.soakHours)}</TableCell>
                <TableCell>{withDays(seed.germination)}</TableCell>
                <TableCell>{withDays(seed.blackout)}</TableCell>
                <TableCell
                  sx={{
                    maxWidth: 100,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Tooltip title={<Typography sx={{ fontSize: '0.8rem' }}>{seed.notes || ''}</Typography>}>
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-block',
                        width: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {seed.notes || ''}
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <IconButton onClick={(e) => { e.stopPropagation(); handleMenuOpen(e, seed); }}>
                    <SettingsIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {paginatedSeeds.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No seeds found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={filteredSeeds.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose}>
        <MenuItem disabled={!subscriptionActive} onClick={handleEdit}>
          Edit
        </MenuItem>
        <MenuItem disabled={!subscriptionActive} onClick={handleClone}>
          Clone
        </MenuItem>
        <MenuItem onClick={() => { handleMenuClose(); openConfirmDialog(selectedSeed); }}>
          Delete
        </MenuItem>
      </Menu>
      <AddSeed
        open={open}
        onClose={handleCloseDialog}
        onAdd={handleAddSeed}
        editMode={editMode}
        seedToEdit={seedToEdit}
        setSuccess={setSuccess}
        setError={setError}
      />

      <ConfirmationDialog
        open={confirmOpen}
        title="Confirm Seed Deletion"
        message="Are you sure you want to delete this seed?"
        onConfirm={handleConfirmDelete}
        onClose={handleConfirmCancel}
      />
      {/* InfoModal for seed details */}
      <InfoModalV2
        open={infoModalOpen}
        onClose={() => setInfoModalOpen(false)}
        data={selectedSeedData}
        schema={seedSchema}
      />

    </Box>
  );
}

export default Seeds;

function isSubscriptionActive(userData) {
  if (!userData || !userData.accessExpires) {
    return true;
  }
  const now = new Date();
  const expires = userData.accessExpires.toDate
    ? userData.accessExpires.toDate()
    : new Date(userData.accessExpires);
  return now <= expires;
}


